import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { ssoLoginActions, ssoLoginSelectors } from '../reducer/sso-login-reducer';
import { parse } from 'query-string';
import { getAndClearMobileLoginSessionData } from '../services/sso-helpers';
import { lionSelectors, LionTranslate } from '@rosetta/react-lion/dist/index';
import { mobileTextGrey, warningRedColor, lighterGrey, white } from '../../../styles/colors';
import { MobileButton } from '../../../styles/MobileButton';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { hiResBgImage } from '../../../styles/mixins';
import loResLockIcon from '../../../assets/images/mobile_icon_password_black.png';
import hiResLockIcon from '../../../assets/images/mobile_icon_password_black_2x.png';
import loResRedLockIcon from '../../../assets/images/mobile_icon_password_red.png';
import hiResRedLockIcon from '../../../assets/images/mobile_icon_password_red_2x.png';
import loResResetIcon from '../../../assets/images/mobile_icon_close.png';
import hiResResetIcon from '../../../assets/images/mobile_icon_close_2x.png';
import { isValidInterfaceLanguage } from '../../app/services/language-helpers';
import { appActions, LOG_LEVEL } from '../../app/reducer/app-reducer';
import EndInteraction from '../../app/components/EndInteraction';

/*
  MobileSsoLogin is where anyone who navigated to /mobilessologin goes.
  They could be coming here because the mobile app sent them here,
  or because after that they successfully logged in and were routed
  back to here by routeSsoFlow().

  In the first case they may need to enter their org namespace
  aka sso_id, or it may have been supplied by query params.  This
  page provides for that.

  In the second case they will either be routed to an error page
  or to the launchpad page aka dashboard.
*/

const boxWidth = 280;
const lockWidth = 18;
const lockLeft = 4;
const resetInputWidth = 23;

const MobileSsoLoginDiv = styled.div(() => {
  let style = {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'scroll',
    backgroundColor: white,
    cursor: 'default'
  };
  return style;
});

const MobileSsoLoginBox = styled.div(() => {
  let style = {
    position: 'relative',
    width: boxWidth + 'px',
    margin: '40px auto 0px auto',
    whiteSpace: 'normal'
  };
  return style;
});

const TitleText = styled.div(() => {
  let style = {
    fontSize: '20px',
    fontWeight: '500',
    lineHeight: '110%',
    textAlign: 'center'
  };
  return style;
});

const ExplanatoryText = styled.div(() => {
  let style = {
    marginTop: '18px',
    marginBottom: '5px',
    paddingLeft: '0px',
    paddingRight: '0px',
    fontSize: '14px',
    lineHeight: '130%',
    textAlign: 'center',
    color: mobileTextGrey
  };
  return style;
});

const ErrorDisp = styled.div(() => {
  let style = {
    display: 'block',
    height: '36px',
    paddingTop: '17px',
    paddingLeft: '4px',
    fontSize: '14px',
    lineHeight: '130%',
    color: warningRedColor
  };
  return style;
});

const InputContainer = styled.div(() => {
  let style = {
    display: 'block',
    position: 'relative',
    width: '100%',
    height: '48px',
    marginBottom: '20px',
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: '1px solid ' + lighterGrey,
    borderLeft: 'none',
    outline: 'none'
  };
  return style;
});

const LockIcon = styled.div((props) => {
  const lockImgWidth = '14px';
  const lockImgHeight = '18px';
  let style = {
    display: 'block',
    position: 'absolute',
    left: lockLeft + 'px',
    top: '15px',
    height: lockWidth + 'px',
    width: lockWidth + 'px',
    backgroundPosition: '2px 0px',
    backgroundRepeat: 'no-repeat',
    ...hiResBgImage(loResLockIcon, hiResLockIcon, lockImgWidth, lockImgHeight)
  };
  if (props.hasError) {
    style = {
      ...style,
      ...hiResBgImage(loResRedLockIcon, hiResRedLockIcon, lockImgWidth, lockImgHeight)
    };
  }
  return style;
});

const SsoCodeInput = styled.input(() => {
  let style = {
    display: 'block',
    position: 'absolute',
    top: '9px',
    left: lockWidth + lockLeft + 'px',
    backgroundColor: white,
    height: '34px',
    width: boxWidth - (lockLeft + lockWidth + resetInputWidth) + 'px',
    paddingLeft: '12px',
    fontSize: '18px',
    border: 'none',
    borderRadius: '0px',
    outline: 'none',
    fontFamily: 'NotoSans, NotoSans_Hebrew, NotoSans_Arabic, NotoSans_Hindi, NotoSans_JP, NotoSans_KR, NotoSans_TC, NotoSans_SC'
  };
  return style;
});

const ResetInputButton = styled.div(() => {
  let style = {
    display: 'block',
    position: 'absolute',
    right: '0px',
    top: '15px',
    height: '22px',
    width: resetInputWidth + 'px',
    backgroundPosition: '4px 4px',
    backgroundRepeat: 'no-repeat',
    ...hiResBgImage(loResResetIcon, hiResResetIcon, '13px', '14px'),
    cursor: 'pointer',
    outline: 'none'
  };
  return style;
});

//*********************************************
export class MobileSsoLogin extends PureComponent {
  componentWillMount = () => {
    this.setState({
      tempSsoCode: ''
    });

    let mobileSSOLoginData = getAndClearMobileLoginSessionData();
    if (mobileSSOLoginData && mobileSSOLoginData.token) {
      // case where sso login succeeded
      this.props.finishMobileSsoLogin(mobileSSOLoginData);
    } else {
      const queryParams = parse(this.props.location.search);
      if (!queryParams.app_id) {
        this.props.logEvent({
          level: LOG_LEVEL.ERROR,
          message: 'came to mobilessologin with no app_id param'
        });
        this.props.setMobileSsoLoginError('_mobile_sso_wrong_app');
      } else if (queryParams.sso_code) {
        // case where initiating login and already have an sso code from previous success
        this.props.attemptMobileSsoLogin(queryParams);
      }

      // show the page and let user fill in the namespace/ssocode
      if (isValidInterfaceLanguage(queryParams.ui_language)) {
        this.props.setInterfaceLanguage(queryParams.ui_language);
      }
    }
  };

  mapEnterToAction = (event) => {
    if (event.key === 'Enter' && !this.isSubmitButtonDisabled()) {
      this.handleSubmitButtonClick();
    }
  };

  isUIDisabled = () => {
    return this.props.ssoLoginAttemptPending;
  };

  isSubmitButtonDisabled = () => {
    return this.isUIDisabled() || this.props.mobileSsoLoginError.length > 0 || this.state.tempSsoCode.length < 2;
  };

  handleInputChanged = (event) => {
    this.props.setMobileSsoLoginError('');
    this.setState({ tempSsoCode: event.target.value });
  };

  handleSubmitButtonClick = () => {
    let queryParams = parse(this.props.location.search);
    queryParams.sso_code = this.state.tempSsoCode;
    this.props.attemptMobileSsoLogin(queryParams);
  };

  showInputResetButton = () => {
    return this.state.tempSsoCode.length > 0;
  };

  resetInput = () => {
    this.setState({ tempSsoCode: '' });
  };

  render = () => {
    const mouseCatcher = this.isUIDisabled() ? <MouseCatcher /> : null;

    let resetInputButton = null;
    if (this.showInputResetButton()) {
      resetInputButton = <ResetInputButton onClick={this.resetInput} />;
    }

    let errorDisp = <ErrorDisp data-qa="ErrorDispEmpty" />;
    if (this.props.mobileSsoLoginError) {
      errorDisp = (
        <ErrorDisp data-qa="ErrorDispErrorMit">
          <LionTranslate lionkey={this.props.mobileSsoLoginError} />
        </ErrorDisp>
      );
    }

    let widgets = null;
    if (!this.props.ssoLoginAttemptPending && this.props.lionLoaded) {
      widgets = (
        <MobileSsoLoginBox data-qa="MobileSsoLoginBox">
          <TitleText data-qa="TitleText">
            <LionTranslate lionkey="_mobile_sso_title" />
          </TitleText>
          <ExplanatoryText>
            <LionTranslate lionkey="_mobile_sso_explanation" />
          </ExplanatoryText>
          {errorDisp}
          <InputContainer>
            <LockIcon hasError={this.props.mobileSsoLoginError.length > 0} />
            <SsoCodeInput
              data-qa="SsoCodeInput"
              type="text"
              value={this.state.tempSsoCode}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              inputMode="verbatim"
              spellCheck="false"
              disabled={this.isUIDisabled()}
              onChange={this.handleInputChanged}
              onKeyPress={this.mapEnterToAction}
              placeholder={this.props.lionTranslateString('_mobile_sso_company_code_placeholder')}
              tabindex="0"
            />
            {resetInputButton}
          </InputContainer>
          <MobileButton
            data-qa="SubmitButton"
            disabled={this.isSubmitButtonDisabled()}
            onClick={this.handleSubmitButtonClick}
          >
            <LionTranslate lionkey="_mobile_sso_next" />
          </MobileButton>
        </MobileSsoLoginBox>
      );
    }

    return (
      <MobileSsoLoginDiv data-qa="MobileSsoLoginDiv">
        {/* event will be sent after user authenticates */}
        <EndInteraction interaction="application-startup" />
        {widgets}
        {mouseCatcher}
      </MobileSsoLoginDiv>
    );
  };
}

const mapStateToProps = (state, ownprops) => ({
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  lionLanguage: lionSelectors.getLionLanguage(state),
  lionTranslateString: (key, placeholders, values) => {
    return lionSelectors.getLionTranslatedString(state, key, placeholders, values);
  },
  ssoLoginAttemptPending: ssoLoginSelectors.ssoLoginAttemptPending(state),
  mobileSsoLoginError: ssoLoginSelectors.mobileSsoLoginError(state)
});

const wrapActions = {
  logEvent: appActions.logEvent,
  setMobileSsoLoginError: ssoLoginActions.setMobileSsoLoginError,
  attemptMobileSsoLogin: ssoLoginActions.attemptMobileSsoLogin,
  finishMobileSsoLogin: ssoLoginActions.finishMobileSsoLogin,
  setInterfaceLanguage: appActions.setInterfaceLanguage
};
export const MobileSsoLoginContainer = connect(
  mapStateToProps,
  wrapActions
)(MobileSsoLogin);



// WEBPACK FOOTER //
// ./src/modules/login/components/MobileSsoLogin.jsx